import CameraNotificationRules from '../../components/cameras/CameraNotificationRules';
import CameraEventAudio from '../../components/cameras/CameraEventAudio';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import { ArrowLeft, Save } from 'lucide-react';
import { cameraService, locationService } from '../../services';
import type { Camera, Location } from '../../types';

const EditCameraPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [camera, setCamera] = useState<Camera | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    camera_location: '',
    camera_channel: '',
    camera_purpose: '',
    is_id_camera: false,
    brand: '',
    model: '',
    serial_number: '',
    firmware_version: '',
    camera_mac: '',
    ip_address: '',
    http_port: '',
    onvif_port: '',
    rtsp_port: '',
    username: '',
    encrypted_password: '',
    primary_protocol: '',
    camera_stream_url: '',
    camera_installation: '',
    camera_last_maintenance: '',
    health_check_interval: '',
    event_polling_interval: '',
    location_id: '',
    status: true,
    // Enabled features
    enabled_features: {
      motion_detection: false,
      line_crossing: false,
      intrusion_detection: false,
      face_detection: false,
      vehicle_detection: false,
      license_plate: false,
      audio: false,
      ptz: false,
    } as Record<string, boolean>,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (id) {
      fetchCamera(parseInt(id));
    }
    fetchLocations();
  }, [id]);

  const fetchCamera = async (cameraId: number) => {
    try {
      setIsLoading(true);
      setError(null);
      const cam = await cameraService.getCameraById(cameraId);
      setCamera(cam);
      setFormData({
        name: cam.name || cam.camera_location || '',
        camera_location: cam.camera_location || '',
        camera_channel: cam.camera_channel || '',
        camera_purpose: cam.camera_purpose || '',
        is_id_camera: cam.is_id_camera || false,
        brand: cam.brand || '',
        model: cam.model || cam.camera_model || '',
        serial_number: cam.serial_number || '',
        firmware_version: cam.firmware_version || '',
        camera_mac: cam.camera_mac || '',
        ip_address: cam.ip_address || cam.camera_ip || '',
        http_port: (cam.http_port || cam.camera_port)?.toString() || '',
        onvif_port: cam.onvif_port?.toString() || '',
        rtsp_port: cam.rtsp_port?.toString() || '',
        username: cam.username || cam.camera_username || '',
        encrypted_password: cam.encrypted_password || cam.camera_password || '',
        primary_protocol: cam.primary_protocol || '',
        camera_stream_url: cam.camera_stream_url || '',
        camera_installation: cam.camera_installation || '',
        camera_last_maintenance: cam.camera_last_maintenance || '',
        health_check_interval: cam.health_check_interval?.toString() || '30',
        event_polling_interval: cam.event_polling_interval?.toString() || '5',
        location_id: cam.location_id?.toString() || '',
        status: cam.status !== false,
        enabled_features: {
          motion_detection: cam.enabled_features?.motion_detection || false,
          line_crossing: cam.enabled_features?.line_crossing || false,
          intrusion_detection: cam.enabled_features?.intrusion_detection || false,
          face_detection: cam.enabled_features?.face_detection || false,
          vehicle_detection: cam.enabled_features?.vehicle_detection || false,
          license_plate: cam.enabled_features?.license_plate || false,
          audio: cam.enabled_features?.audio || false,
          ptz: cam.enabled_features?.ptz || false,
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch camera');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await locationService.getLocations();
      setLocations(response.data || []);
    } catch (err) {
      console.error('Failed to fetch locations:', err);
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim() && !formData.camera_location.trim()) {
      errors.name = 'Camera name is required';
    }
    if (!formData.ip_address.trim()) errors.ip_address = 'IP address is required';
    if (!formData.location_id) errors.location_id = 'Location is required';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!camera || !validateForm()) return;

    try {
      setIsSaving(true);
      const payload: any = {
        name: formData.name.trim(),
        camera_location: formData.camera_location.trim() || formData.name.trim(),
        location_id: parseInt(formData.location_id),
        status: formData.status,
      };

      // Optional string fields
      if (formData.camera_channel) payload.camera_channel = formData.camera_channel;
      if (formData.camera_purpose) payload.camera_purpose = formData.camera_purpose;
      if (formData.brand) payload.brand = formData.brand;
      if (formData.model) payload.model = formData.model;
      if (formData.serial_number) payload.serial_number = formData.serial_number;
      if (formData.firmware_version) payload.firmware_version = formData.firmware_version;
      if (formData.camera_mac) payload.camera_mac = formData.camera_mac;
      if (formData.ip_address) payload.ip_address = formData.ip_address;
      if (formData.username) payload.username = formData.username;
      // Only send password if it's been changed
      if (formData.encrypted_password && formData.encrypted_password !== (camera.encrypted_password || camera.camera_password)) {
        payload.encrypted_password = formData.encrypted_password;
      }
      if (formData.primary_protocol) payload.primary_protocol = formData.primary_protocol;
      if (formData.camera_stream_url) payload.camera_stream_url = formData.camera_stream_url;
      if (formData.camera_installation) payload.camera_installation = formData.camera_installation;
      if (formData.camera_last_maintenance) payload.camera_last_maintenance = formData.camera_last_maintenance;

      // Numeric fields
      if (formData.http_port) {
        const port = parseInt(formData.http_port);
        if (!isNaN(port)) payload.http_port = port;
      }
      if (formData.onvif_port) {
        const port = parseInt(formData.onvif_port);
        if (!isNaN(port)) payload.onvif_port = port;
      }
      if (formData.rtsp_port) {
        const port = parseInt(formData.rtsp_port);
        if (!isNaN(port)) payload.rtsp_port = port;
      }
      if (formData.health_check_interval) {
        const interval = parseInt(formData.health_check_interval);
        if (!isNaN(interval)) payload.health_check_interval = interval;
      }
      if (formData.event_polling_interval) {
        const interval = parseInt(formData.event_polling_interval);
        if (!isNaN(interval)) payload.event_polling_interval = interval;
      }

      // Boolean fields - only send if explicitly set
      if (formData.is_id_camera !== undefined) {
        payload.is_id_camera = formData.is_id_camera;
      }

      // Enabled features
      payload.enabled_features = formData.enabled_features;

      // Validate location_id is a valid number
      if (isNaN(payload.location_id)) {
        setError('Invalid location ID');
        setIsSaving(false);
        return;
      }

      console.log('Camera Edit Payload:', JSON.stringify(payload, null, 2));
      console.log('Payload types:', {
        camera_id: typeof payload.camera_id,
        location_id: typeof payload.location_id,
        http_port: typeof payload.http_port,
        onvif_port: typeof payload.onvif_port,
        rtsp_port: typeof payload.rtsp_port,
        health_check_interval: typeof payload.health_check_interval,
        event_polling_interval: typeof payload.event_polling_interval,
        status: typeof payload.status,
        is_id_camera: typeof payload.is_id_camera,
      });

      try {
        await cameraService.updateCamera(camera.camera_id, payload);
        // Success - navigate back
        navigate('/cameras');
      } catch (apiErr) {
        // Backend is updating the data but crashing on response
        // Check if it's a network error (which means backend crashed after update)
        if (apiErr instanceof TypeError && apiErr.message.includes('Failed to fetch')) {
          console.warn('Backend crashed after update, but data may have been saved. Navigating back...');
          // Give it a moment then navigate (data was likely saved)
          setTimeout(() => navigate('/cameras'), 1000);
        } else {
          // Real error - show it
          throw apiErr;
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update camera');
      console.error('Camera update error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-20">
          <Loading size="lg" />
        </div>
      </MainLayout>
    );
  }

  if (error && !camera) {
    return (
      <MainLayout>
        <Alert variant="error" title="Error" message={error} />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => navigate('/cameras')}
              leftIcon={<ArrowLeft size={16} />}
            >
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-text-primary">Edit Camera</h1>
              <p className="text-text-muted">{camera?.name || camera?.camera_location}</p>
            </div>
          </div>
          <Button
            type="submit"
            variant="primary"
            isLoading={isSaving}
            leftIcon={<Save size={16} />}
          >
            Save Changes
          </Button>
        </div>

        {error && (
          <Alert variant="error" title="Error" message={error} />
        )}

        {/* Basic Information */}
        <Card padding="lg">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Basic Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Camera Name *"
              placeholder="e.g., Front Gate Camera"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={formErrors.name}
              fullWidth
            />
            <Input
              label="Camera Location Label"
              placeholder="e.g., Front Gate"
              value={formData.camera_location}
              onChange={(e) => setFormData({ ...formData, camera_location: e.target.value })}
              fullWidth
            />
            <Input
              label="Camera Channel"
              placeholder="e.g., 101"
              value={formData.camera_channel}
              onChange={(e) => setFormData({ ...formData, camera_channel: e.target.value })}
              fullWidth
            />
            <Select
              label="Location *"
              value={formData.location_id}
              onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
              error={formErrors.location_id}
              options={[
                { label: 'Select Location', value: '' },
                ...locations.map(loc => ({ label: loc.ycl_name, value: loc.ycl_id.toString() })),
              ]}
              fullWidth
            />
            <Select
              label="Camera Purpose"
              value={formData.camera_purpose}
              onChange={(e) => setFormData({ ...formData, camera_purpose: e.target.value })}
              options={[
                { label: 'Select Purpose', value: '' },
                { label: 'General', value: 'general' },
                { label: 'ID Capture', value: 'id_capture' },
                { label: 'Face Recognition', value: 'face_recognition' },
                { label: 'License Plate', value: 'license_plate' },
                { label: 'Vehicle Front', value: 'vehicle_front' },
                { label: 'Vehicle Right', value: 'vehicle_right' },
                { label: 'Vehicle Left', value: 'vehicle_left' },
                { label: 'Vehicle Back', value: 'vehicle_back' },
              ]}
              fullWidth
            />
            <div className="flex items-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_id_camera}
                  onChange={(e) => setFormData({ ...formData, is_id_camera: e.target.checked })}
                  className="w-4 h-4 text-accent-primary bg-surface-secondary border-surface-tertiary rounded focus:ring-accent-primary"
                />
                <span className="text-sm text-text-primary">ID Camera</span>
              </label>
            </div>
          </div>
        </Card>

        {/* Device Information */}
        <Card padding="lg">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Device Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Brand"
              placeholder="e.g., Hikvision"
              value={formData.brand}
              onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
              fullWidth
            />
            <Input
              label="Model"
              placeholder="e.g., DS-2CD2385G1-I"
              value={formData.model}
              onChange={(e) => setFormData({ ...formData, model: e.target.value })}
              fullWidth
            />
            <Input
              label="Serial Number"
              placeholder="e.g., SN123456789"
              value={formData.serial_number}
              onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
              fullWidth
            />
            <Input
              label="Firmware Version"
              placeholder="e.g., V5.7.3"
              value={formData.firmware_version}
              onChange={(e) => setFormData({ ...formData, firmware_version: e.target.value })}
              fullWidth
            />
            <Input
              label="MAC Address"
              placeholder="e.g., 00:11:22:33:44:55"
              value={formData.camera_mac}
              onChange={(e) => setFormData({ ...formData, camera_mac: e.target.value })}
              fullWidth
            />
          </div>
        </Card>

        {/* Network Configuration */}
        <Card padding="lg">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Network Configuration</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="IP Address *"
              placeholder="e.g., 192.168.1.100"
              value={formData.ip_address}
              onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
              error={formErrors.ip_address}
              fullWidth
            />
            <Input
              label="HTTP Port"
              type="number"
              placeholder="e.g., 80"
              value={formData.http_port}
              onChange={(e) => setFormData({ ...formData, http_port: e.target.value })}
              fullWidth
            />
            <Input
              label="ONVIF Port"
              type="number"
              placeholder="e.g., 80"
              value={formData.onvif_port}
              onChange={(e) => setFormData({ ...formData, onvif_port: e.target.value })}
              fullWidth
            />
            <Input
              label="RTSP Port"
              type="number"
              placeholder="e.g., 554"
              value={formData.rtsp_port}
              onChange={(e) => setFormData({ ...formData, rtsp_port: e.target.value })}
              fullWidth
            />
            <Input
              label="Username"
              placeholder="e.g., admin"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              fullWidth
            />
            <Input
              label="Password"
              type="password"
              placeholder="Enter password"
              value={formData.encrypted_password}
              onChange={(e) => setFormData({ ...formData, encrypted_password: e.target.value })}
              fullWidth
            />
          </div>
        </Card>

        {/* Protocol Configuration */}
        <Card padding="lg">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Protocol Configuration</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Primary Protocol"
              value={formData.primary_protocol}
              onChange={(e) => setFormData({ ...formData, primary_protocol: e.target.value })}
              options={[
                { label: 'Select Protocol', value: '' },
                { label: 'ONVIF', value: 'ONVIF' },
                { label: 'RTSP', value: 'RTSP' },
                { label: 'WebRTC', value: 'WebRTC' },
                { label: 'HTTP', value: 'HTTP' },
              ]}
              fullWidth
            />
            <Input
              label="Custom Stream URL"
              placeholder="e.g., rtsp://camera/stream"
              value={formData.camera_stream_url}
              onChange={(e) => setFormData({ ...formData, camera_stream_url: e.target.value })}
              fullWidth
            />
          </div>
        </Card>

        {camera && <CameraEventAudio cameraId={camera.camera_id} />}
        {camera && <CameraNotificationRules cameraId={camera.camera_id} />}

        {/* Monitoring Configuration */}
        <Card padding="lg">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Monitoring Configuration</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Health Check Interval (seconds)"
              type="number"
              placeholder="30"
              value={formData.health_check_interval}
              onChange={(e) => setFormData({ ...formData, health_check_interval: e.target.value })}
              fullWidth
            />
            <Input
              label="Event Polling Interval (seconds)"
              type="number"
              placeholder="5"
              value={formData.event_polling_interval}
              onChange={(e) => setFormData({ ...formData, event_polling_interval: e.target.value })}
              fullWidth
            />
          </div>
        </Card>

        {/* Installation & Maintenance */}
        <Card padding="lg">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Installation & Maintenance</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Installation Date"
              type="date"
              value={formData.camera_installation}
              onChange={(e) => setFormData({ ...formData, camera_installation: e.target.value })}
              fullWidth
            />
            <Input
              label="Last Maintenance Date"
              type="date"
              value={formData.camera_last_maintenance}
              onChange={(e) => setFormData({ ...formData, camera_last_maintenance: e.target.value })}
              fullWidth
            />
          </div>
        </Card>

        {/* Settings */}
        <Card padding="lg">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Settings</h3>
          <div className="flex items-center">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.checked })}
                className="w-4 h-4 text-accent-primary bg-surface-secondary border-surface-tertiary rounded focus:ring-accent-primary"
              />
              <span className="text-sm text-text-primary">Active</span>
            </label>
          </div>
        </Card>

        {/* Enabled Features */}
        <Card padding="lg">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Enabled Features</h3>
          <p className="text-sm text-text-muted mb-4">Select the features enabled for this camera</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.enabled_features?.motion_detection || false}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  enabled_features: { ...formData.enabled_features, motion_detection: e.target.checked }
                })}
                className="w-4 h-4 text-accent-primary bg-surface-secondary border-surface-tertiary rounded focus:ring-accent-primary"
              />
              <span className="text-sm text-text-primary">Motion Detection</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.enabled_features?.line_crossing || false}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  enabled_features: { ...formData.enabled_features, line_crossing: e.target.checked }
                })}
                className="w-4 h-4 text-accent-primary bg-surface-secondary border-surface-tertiary rounded focus:ring-accent-primary"
              />
              <span className="text-sm text-text-primary">Line Crossing</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.enabled_features?.intrusion_detection || false}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  enabled_features: { ...formData.enabled_features, intrusion_detection: e.target.checked }
                })}
                className="w-4 h-4 text-accent-primary bg-surface-secondary border-surface-tertiary rounded focus:ring-accent-primary"
              />
              <span className="text-sm text-text-primary">Intrusion Detection</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.enabled_features?.face_detection || false}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  enabled_features: { ...formData.enabled_features, face_detection: e.target.checked }
                })}
                className="w-4 h-4 text-accent-primary bg-surface-secondary border-surface-tertiary rounded focus:ring-accent-primary"
              />
              <span className="text-sm text-text-primary">Face Detection</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.enabled_features?.vehicle_detection || false}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  enabled_features: { ...formData.enabled_features, vehicle_detection: e.target.checked }
                })}
                className="w-4 h-4 text-accent-primary bg-surface-secondary border-surface-tertiary rounded focus:ring-accent-primary"
              />
              <span className="text-sm text-text-primary">Vehicle Detection</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.enabled_features?.license_plate || false}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  enabled_features: { ...formData.enabled_features, license_plate: e.target.checked }
                })}
                className="w-4 h-4 text-accent-primary bg-surface-secondary border-surface-tertiary rounded focus:ring-accent-primary"
              />
              <span className="text-sm text-text-primary">License Plate (LPR)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.enabled_features?.audio || false}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  enabled_features: { ...formData.enabled_features, audio: e.target.checked }
                })}
                className="w-4 h-4 text-accent-primary bg-surface-secondary border-surface-tertiary rounded focus:ring-accent-primary"
              />
              <span className="text-sm text-text-primary">Audio</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.enabled_features?.ptz || false}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  enabled_features: { ...formData.enabled_features, ptz: e.target.checked }
                })}
                className="w-4 h-4 text-accent-primary bg-surface-secondary border-surface-tertiary rounded focus:ring-accent-primary"
              />
              <span className="text-sm text-text-primary">PTZ Control</span>
            </label>
          </div>
        </Card>
      </form>
    </MainLayout>
  );
};

export default EditCameraPage;
